import React from "react";
import { useParams } from "react-router-dom";
import { useAppSelector } from "../../../../app/hooks";
import { selectGroupById } from "../../../../features/groups/groupSlice";
import { invoiceCreator, getStatusTextColor, InvoiceStatus } from "../utils";
import InvoiceTable from "./InvoiceTable";
import { LoadingSpinner } from "../../../../utils/StateDisplay";
import { Invoice } from "../../../../features/groups/groupSlice";
import useGetLanguage from "../../../../utils/useGetLanguage";
import { LanguageText } from "../../../../dictionary/GroupsText";

const Invoices: React.FC = (): JSX.Element => {
  const language: LanguageText = useGetLanguage({
    fileName: "GroupsText",
  });
  const { groupid } = useParams<{ groupid: string }>();
  const groupInfo = useAppSelector((state) => selectGroupById(state, groupid ?? ""));

  const invoices = groupInfo?.subscription?.invoices ?? [];
  //const invoices = invoiceCreator(200); // For testing

  const hasNoInvoices = invoices.length === 0;

  return (
    <div className="space-y-6">
      {hasNoInvoices ? (
        <div className="text-center py-16">
          <div className="mx-auto h-32 w-32 text-gray-300 mb-6">
            <svg
              className="h-full w-full"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {language?.groupSubscription?.noInvoicesAvailable}
          </h3>
          <p className="text-gray-500 max-w-md mx-auto">
            {language?.groupSubscription?.noInvoicesDescription}
          </p>
        </div>
      ) : (
        <>
          {/* Summary stats */}
          <div className="bg-gray-100 rounded-lg shadow-md p-6 border border-gray-200 max-w-fit mx-auto sm:ml-auto sm:mr-0">
            <div className="flex justify-center sm:justify-end">
              <div className="flex flex-wrap gap-6 text-sm">
                <div className="text-center">
                  <div
                    className={`text-2xl font-bold ${getStatusTextColor(
                      InvoiceStatus.PAID
                    )}`}
                  >
                    {
                      invoices.filter((inv) => inv.invoice_status === InvoiceStatus.PAID)
                        .length
                    }
                  </div>
                  <div className="text-gray-600">{language?.groupSubscription?.paid}</div>
                </div>

                <div className="text-center">
                  <div
                    className={`text-2xl font-bold ${getStatusTextColor(
                      InvoiceStatus.PENDING
                    )}`}
                  >
                    {
                      invoices.filter(
                        (inv) => inv.invoice_status === InvoiceStatus.PENDING
                      ).length
                    }
                  </div>
                  <div className="text-gray-600">
                    {language?.groupSubscription?.pending}
                  </div>
                </div>

                <div className="text-center">
                  <div
                    className={`text-2xl font-bold ${getStatusTextColor(
                      InvoiceStatus.OVERDUE
                    )}`}
                  >
                    {
                      invoices.filter(
                        (inv) => inv.invoice_status === InvoiceStatus.OVERDUE
                      ).length
                    }
                  </div>
                  <div className="text-gray-600">
                    {language?.groupSubscription?.overdue}
                  </div>
                </div>

                <div className="text-center">
                  <div
                    className={`text-2xl font-bold ${getStatusTextColor(
                      InvoiceStatus.CANCELLED
                    )}`}
                  >
                    {
                      invoices.filter(
                        (inv) => inv.invoice_status === InvoiceStatus.CANCELLED
                      ).length
                    }
                  </div>
                  <div className="text-gray-600">
                    {language?.groupSubscription?.cancelled}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Invoice Table */}
          <InvoiceTable invoices={invoices} />
        </>
      )}
    </div>
  );
};

export default Invoices;
